/* eslint-disable react/jsx-filename-extension */
/* eslint-disable react/jsx-props-no-spreading */
import React, { Component, isValidElement } from 'react';
import T from 'i18n-react';

import {
	Checkbox,
	Input,
	LabelContainer,
	PrefixInput,
	Textarea,
	Tooltip,
	TooltipAnchor,
} from '@motorway/motorway-component-library';
import { FeedbackTooltip, Select } from '@motorway/motorway-storybook-cra';
import { VisuallyHidden } from '@motorway/mw-highway-code/src/components/__primitives__/VisuallyHidden';

import { APP_THEME } from 'Utilities/consts';

import { applyCypressData, isEmpty, isFunction, isString, scrollIntoView } from '../helpers';

import { INTENT_TYPES } from './FormBase.helpers';

import styles from './FormBase.module.scss';

T.setTexts(require('../../components/texts.json'));

class FormBase extends Component {
	// Defaults expected to be provided by inherited module
	INITIAL_STATE = {};

	$form = null;

	submit = null;

	customErrorMessage = null;

	customInputFormat = null;

	customCheckValidity = null;

	customValidation = null;

	customNearestInvalid = null;

	customInputIntent = null;

	customOnBlur = null;

	customOnFocus = null;

	customOnChange = null;

	customOnKeyUp = null;

	customResetFormStateResolver = null;

	customGetTextInputResolver = null;

	customGetCheckboxInputResolver = null;

	customGetTextareaInputResolver = null;

	validationInterceptor = null;

	showIfInvalid = true;

	customShowIfInvalid = null;

	focused = false;

	onBlur = async (e) => {
		const { id } = e.target;
		let valid = this.checkValidity(e.target);

		if (isFunction(this.customOnBlur)) {
			valid = await this.customOnBlur(id, valid, e);
		}

		this.updateInputState(id, { focused: this.focused, showIfInvalid: this.showIfInvalid, valid });
	};

	// Defaults expected to be read only by inherited module
	getInputProp = (id, prop) => {
		const { inputs } = this.state;
		return (inputs.find((i) => i.name === id) || {})[prop];
	};

	getErrorMessage(id, props) {
		let name = id;

		if (isFunction(this.customErrorMessage)) {
			name = this.customErrorMessage(id) || id;

			if (isValidElement(name)) {
				return name;
			}
		}

		return T.translate(`${this.textsPrefix}.errorMessages.${name}`, props);
	}

	getInputFormat = (id, value) => {
		let result = value;

		if (isFunction(this.customInputFormat)) {
			result = this.customInputFormat(id, value);
		}

		return result;
	};

	getValue(id) {
		return this.getInputFormat(id, this.getInputProp(id, 'value'));
	}

	checkValidity = (el) => {
		const isDom = isFunction(el.checkValidity);
		const id = isDom ? el.id : el;
		const value = this.getValue(id);
		let valid = isDom ? el.checkValidity() : this.getInputProp(el, 'valid');

		if (isString(value) && value.replace(/\s/g, '') === '') {
			valid = false;
		}

		if (!this.getInputProp(id, 'required')) {
			valid = true;
		}

		if (isFunction(this.customCheckValidity)) {
			valid = this.customCheckValidity(id, valid, el);
		}

		return valid;
	};

	isSuccessIntent = (id, mustFocus = true) =>
		this.checkValidity(id) && !isEmpty(this.getValue(id)) && (mustFocus ? !this.getFocusState(id) : true);

	isWarningIntent = (id) => this.checkValidity(id) && this.getInputProp(id, 'showIfInvalid');

	isErrorIntent = (id, mustFocus = true) =>
		!this.checkValidity(id) && this.getInputProp(id, 'showIfInvalid') && (mustFocus ? !this.getFocusState(id) : true);

	getFocusState = (id) => this.getInputProp(id, 'focused');

	getInputIntent = (id) => {
		if (isFunction(this.customInputIntent)) {
			return this.customInputIntent(id);
		}

		if (this.isSuccessIntent(id)) {
			return INTENT_TYPES.SUCCESS;
		}

		if (this.isErrorIntent(id)) {
			return INTENT_TYPES.ERROR;
		}

		return null;
	};

	updateInputState = (id, nextState, callback) => {
		this.setState(
			({ inputs }) => ({
				inputs: inputs.map((i) => {
					if (i.name !== id) {
						return i;
					}

					return { ...i, ...nextState };
				}),
			}),
			callback,
		);
	};

	resetForm = (nextState) => {
		this.setState((prevState) => ({
			...this.INITIAL_STATE,
			...(isFunction(nextState) ? nextState(prevState) : nextState),
			...(isFunction(this.customResetFormStateResolver) ? this.customResetFormStateResolver(prevState) : {}),
		}));

		if (this.$form && this.$form.current && isFunction(this.$form.current.reset)) {
			this.$form.current.reset();
		}
	};

	onFocus = (e) => {
		if (isFunction(this.customOnFocus)) {
			this.customOnFocus(e);
		}

		this.updateInputState(e.target.id, { focused: true });
	};

	onChange = (e) => {
		const { id, value } = e.target;

		if (isFunction(this.customOnChange)) {
			this.customOnChange(id, value, e);
		}

		this.updateInputState(id, {
			value: this.getInputFormat(id, value),
		});
	};

	onCheckboxChange = (e) => {
		const { checked, id } = e.target;

		this.updateInputState(id, {
			valid: checked === true,
			value: this.getInputFormat(id, checked),
		});
	};

	getTooltip = (id) => {
		const text = T.translate(`${this.textsPrefix}.tooltip.${id}`, { notFound: '' });

		if (!text) {
			return null;
		}

		return (
			<Tooltip overlay={<div>{text}</div>} placement="right">
				<TooltipAnchor />
			</Tooltip>
		);
	};

	getLabel = ({ label, name, showAsRequired = true }) =>
		label ? `${label}${this.getInputProp(name, 'required') && showAsRequired ? ' *' : ''}` : '';

	getTextInput = (props) => {
		const { cypressId, inputProps, label, labelProps, name, placeholder, showLabel = true, type, ...rest } = props;

		const intent = this.getInputIntent(name);
		const Element = rest.prefix || rest.suffix ? PrefixInput : Input;
		const value = this.getValue(name);
		const resolveLabel = () => {
			const label = this.getLabel(props);

			if (!label) {
				return null;
			}

			if (showLabel) {
				return <LabelContainer label={label} {...labelProps} />;
			}

			return <VisuallyHidden>{label}</VisuallyHidden>;
		};

		return (
			<Element
				feedbackTooltip={{
					content: this.getErrorMessage(name),
				}}
				id={name}
				inputProps={{
					onBlur: this.onBlur,
					onChange: this.onChange,
					onFocus: this.onFocus,
					placeholder,
					required: this.getInputProp(name, 'required'),
					value: value?.toString() || '',
					...inputProps,
					...(cypressId && applyCypressData(cypressId)),
				}}
				intent={intent}
				label={resolveLabel()}
				name={name}
				type={type}
				{...rest}
				{...(isFunction(this.customGetTextInputResolver) ? this.customGetTextInputResolver(props) : {})}
			/>
		);
	};

	getCheckboxInput = (props) => {
		const { className, cypressId, inputProps, name, ...rest } = props;

		const intent = !this.checkValidity(name) && this.getInputProp(name, 'showIfInvalid') ? 'error' : null;

		return (
			<Checkbox
				className={className}
				feedbackTooltip={{
					content: this.getErrorMessage(name),
				}}
				id={name}
				inputProps={{
					checked: this.getValue(name),
					onBlur: this.onBlur,
					onChange: this.onCheckboxChange,
					onFocus: this.onFocus,
					required: this.getInputProp(name, 'required'),
					...(cypressId && applyCypressData(cypressId)),
					...inputProps,
				}}
				intent={intent}
				label={this.getLabel(props)}
				name={name}
				{...rest}
				{...(isFunction(this.customGetCheckboxInputResolver) ? this.customGetCheckboxInputResolver(props) : {})}
			/>
		);
	};

	getSelectInput = (props) => {
		const { cypressId, name, options, placeholder } = props;

		const intent = this.getInputIntent(name);

		return (
			<div className={styles.selectContainer}>
				<FeedbackTooltip appendTo="parent" content={this.getErrorMessage(name)} intent={intent} theme="dark">
					<Select
						id={name}
						intent={intent}
						label={<LabelContainer label={this.getLabel(props)} labelAfter={this.getTooltip(name)} />}
						name={name}
						selectProps={{
							onBlur: this.onBlur,
							onChange: this.onChange,
							onFocus: this.onFocus,
							required: this.getInputProp(name, 'required'),
							...(cypressId && applyCypressData(cypressId)),
						}}
						theme={APP_THEME}
						value={this.getValue(name) || ''}
					>
						<option value="">{placeholder}</option>
						{options.map((o) => (
							<option key={o.value} value={o.value}>
								{o.text}
							</option>
						))}
					</Select>
				</FeedbackTooltip>
			</div>
		);
	};

	getTextareaInput = (props) => {
		const {
			// Linting rule disabled because we want to remove the label property from the rest object
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			label,
			name,
			placeholder,
			...rest
		} = props;

		const intent = this.getInputIntent(name);

		return (
			<Textarea
				feedbackTooltip={{
					content: this.getErrorMessage(name),
				}}
				id={name}
				intent={intent}
				label={this.getLabel(props)}
				name={name}
				textareaProps={{
					id: name,
					onBlur: this.onBlur,
					onChange: this.onChange,
					placeholder,
					required: this.getInputProp(name, 'required'),
					value: this.getValue(name).toString(),
				}}
				{...rest}
				{...(isFunction(this.customGetTextareaInputResolver) ? this.customGetTextareaInputResolver(props) : {})}
			/>
		);
	};

	validateForm = () => {
		const { inputs } = this.state;
		const nextInputs = [...inputs];
		let nearestInvalid = null;

		const updateNextInputs = (id, nextState) => {
			const index = inputs.findIndex(({ name }) => name === id);
			if (nextState.valid === false && !nearestInvalid) {
				nearestInvalid = id;
			}
			if (index > -1) {
				nextInputs.splice(index, 1, { ...nextInputs[index], ...nextState });
			}
		};

		Array.from(this.$form.current.elements).forEach((i) =>
			updateNextInputs(i.id, { showIfInvalid: true, valid: this.checkValidity(i) }),
		);
		if (isFunction(this.customValidation)) {
			this.customValidation(updateNextInputs);
		}

		const valid = isFunction(this.validationInterceptor)
			? this.validationInterceptor(nextInputs)
			: nextInputs.every((i) => i.valid);

		this.setState({ inputs: nextInputs });

		if (!valid) {
			if (isFunction(this.customNearestInvalid)) {
				nearestInvalid = this.customNearestInvalid(nearestInvalid);
			}
			const el = document.querySelector(`label[for="${nearestInvalid}"]`) || document.getElementById(nearestInvalid);
			scrollIntoView(el, { behavior: 'smooth' });
		}

		return valid;
	};

	onSubmit = (e) => {
		e.preventDefault();
		const valid = this.validateForm();
		if (valid) {
			if (isFunction(this.submit)) {
				this.submit();
			}
		}
	};
}

export default FormBase;
